/**
 * Меню «Выгрузить» вкладки «Голоса клиента» (#848, #2315): таблица, печатная
 * форма и подборка формулировок клиентов. Все три строятся из тех же строк,
 * что экран, — второго источника правды у выгрузки нет.
 *
 * Меню стоит на каждой вкладке, а не только там, где сейчас есть что
 * выгружать: кнопки, которые то есть, то нет, размывали панель (владелец
 * 16.10). Пока вкладка грузится — кнопка недоступна; выгружать нечего надолго
 * (данные не загрузились, под фильтром темы чисел нет) — меню открывается и
 * говорит почему, а пункты недоступны.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Download, FileDown, Printer, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { exportToCSV } from '@/lib/exportUtils';
import { captureMessage } from '@/lib/sentry';
import { openVoicePrintReport } from '@/lib/print-report/voice';
import {
  localDateKey,
  tableRows,
  wordingRows,
  type VoiceQuestionsExportData,
  type VoiceSection,
  type VoiceSectionExportData,
} from './voice-view-model';
import type { VoiceAudience } from './voice-search';
import { themeFileSuffix, type VoiceExportTheme } from './voice-theme-filter-view-model';

/**
 * The tab and what it exports go together: the section's own tabs export the
 * section's response (its N required), Sales and Site the blocks of their own
 * requests (questions; on Sales the objection stream too).
 */
type VoiceExportSource =
  | {
      audience: Exclude<VoiceAudience, 'sales' | 'site'>;
      data: VoiceSectionExportData;
    }
  | { audience: 'sales' | 'site'; data: VoiceQuestionsExportData };

/**
 * Why the tab has nothing to export: `pending` — its answer is on the way (the
 * button waits); otherwise the menu names the reason
 * (`voicePage.export.unavailable.*`): `failed` — the answer did not load (the
 * page shows its retry), `noNumbers` — no numbers under the theme filter.
 */
export type VoiceExportUnavailable = 'pending' | 'failed' | 'noNumbers';

type VoiceExportProps = VoiceExportSource & {
  sections: readonly VoiceSection[];
  /** Lines the printable report opens with instead of the section's defaults. */
  printNotes?: readonly string[];
  /** The theme filter in force: the files are filed under it (§2.2). */
  theme?: VoiceExportTheme;
  /** The screen shows the previous scope while a new filter loads. */
  disabled: boolean;
};

type VoiceExportMenuProps = VoiceExportProps | { unavailable: VoiceExportUnavailable };

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The exports of a tab with something to export; `wordings` absent — no wording to give. */
function voiceExports(
  { audience, sections, data, printNotes, theme }: VoiceExportProps,
  t: Translate
): { table: () => void; print: () => void; wordings?: () => void } {
  // The code is a slug the organization chose; it goes into the file name so
  // a themed file never passes for the whole tab's.
  const themeSuffix = themeFileSuffix(theme);
  const filenameBase = `voice-${audience}-${localDateKey(data.periodStart)}${themeSuffix}`;

  // A record of each export, content-free: what kind, which tab, how many
  // rows. Through Sentry, not `logInfo` — that one is silent in production.
  const recordExport = (kind: 'table' | 'wordings' | 'print', rows: number) =>
    captureMessage('voice_of_customer.export', 'info', {
      tags: { kind, audience, themed: theme ? 'yes' : 'no' },
      extra: { rows },
    });

  const exportTable = () => {
    const rows = tableRows(sections, data, t, {
      comparable: theme?.prevComparable ?? true,
    });
    recordExport('table', rows.length);
    exportToCSV(rows, filenameBase, {
      section: t('voicePage.export.columns.section'),
      item: t('voicePage.export.columns.item'),
      calls: t('voicePage.export.columns.calls'),
      callsInWindow: t('voicePage.export.columns.callsInWindow'),
      base: t('voicePage.export.columns.base'),
      sharePercent: t('voicePage.export.columns.sharePercent'),
      prevCalls: t('voicePage.export.columns.prevCalls'),
      prevCallsInWindow: t('voicePage.export.columns.prevCallsInWindow'),
      smallSample: t('voicePage.export.columns.smallSample'),
      completeness: t('voicePage.export.columns.completeness'),
      wording: t('voicePage.export.columns.wording'),
    });
  };
  const exportWordings = () => {
    const rows = wordingRows(sections, t);
    recordExport('wordings', rows.length);
    exportToCSV(rows, `${filenameBase}-wordings`, {
      section: t('voicePage.export.columns.section'),
      item: t('voicePage.export.columns.item'),
      wording: t('voicePage.export.columns.wording'),
      detail: t('voicePage.export.columns.detail'),
      date: t('voicePage.export.columns.date'),
      recordId: t('voicePage.export.columns.recordId'),
    });
  };
  const print = () => {
    recordExport(
      'print',
      sections.reduce((sum, section) => sum + section.rows.length, 0)
    );
    openVoicePrintReport(
      {
        audienceLabel: t(`voicePage.audience.${audience}`),
        sections,
        data,
        ...(printNotes ? { notes: printNotes } : {}),
        ...(theme ? { theme } : {}),
      },
      filenameBase
    );
  };

  const hasWordings = sections.some((section) =>
    section.rows.some((row) => row.items.some((item) => item.text !== ''))
  );
  return {
    table: exportTable,
    print,
    ...(hasWordings ? { wordings: exportWordings } : {}),
  };
}

export function VoiceExportMenu(props: VoiceExportMenuProps) {
  const { t } = useTranslation('dashboard');
  const reasonId = useId();
  const reason = 'unavailable' in props ? props.unavailable : null;
  const explained = reason !== null && reason !== 'pending';
  const exports = 'unavailable' in props ? null : voiceExports(props, t);
  const disabled = reason === 'pending' || ('disabled' in props && props.disabled);
  const items = [
    { key: 'table', icon: FileDown, run: exports?.table },
    { key: 'print', icon: Printer, run: exports?.print },
    { key: 'wordings', icon: Quote, run: exports?.wordings },
  ] as const;
  return (
    <DropdownMenu modal={false}>
      {/* On the trigger, not only on the button: Radix opens the menu on its
          own pointer and key handlers, which the button's DOM attribute does
          not stop. */}
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          size="sm"
          className="group gap-2 text-xs"
          disabled={disabled}
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          {t('voicePage.export.menu')}
          <ChevronDown
            aria-hidden="true"
            className="h-4 w-4 text-fg-muted transition-transform group-data-[state=open]:rotate-180"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64"
        // A screen reader announces the menu's items only: the reason is tied
        // to the menu, or the three disabled items come without it.
        aria-describedby={explained ? reasonId : undefined}
      >
        {explained && (
          <p id={reasonId} className="px-3 py-2 text-xs text-fg-muted">
            {t(`voicePage.export.unavailable.${reason}`)}
          </p>
        )}
        {items.map(({ key, icon: Icon, run }) => (
          // A menu left open when the tab turns disabled (a failed refetch, a
          // new filter) must not export the scope the screen no longer shows.
          <DropdownMenuItem
            key={key}
            disabled={disabled || !run}
            onSelect={run}
            className="gap-2"
          >
            <Icon className="h-4 w-4 text-fg-muted" aria-hidden="true" />
            {t(`voicePage.export.${key}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
